import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBarByKey, toggleUpvote, addComment } from '../helpers/api';
import UpvoteButton from '../components/UpvoteButton';
import { FeaturePills } from '../components/FeaturePills';
import CommentTree from '../components/CommentTree';
import { formatNameWithInitials, formatMusicStyles } from '../helpers/constants';

export default function BarDetailPage({ userId }) {
  const { key } = useParams();
  const navigate = useNavigate();

  const [bar, setBar] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newCommentText, setNewCommentText] = useState('');
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadBarDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBarByKey(key, userId);
      setBar(data.bar);
      setComments(data.comments || []);
    } catch (err) {
      console.error('Error loading bar details:', err);
      setError(err.message || 'Erro ao carregar detalhes do bar');
    } finally {
      setLoading(false);
    }
  }, [key, userId]);

  useEffect(() => {
    loadBarDetails();
  }, [loadBarDetails]);

  const handleToggleUpvote = async () => {
    if (!userId) {
      navigate('/login');
      return;
    }

    try {
      const result = await toggleUpvote(userId, bar.id);
      setBar(prev => ({
        ...prev,
        upvoteCount: result.upvoteCount,
        hasUpvoted: result.hasUpvoted
      }));
    } catch (err) {
      console.error('Error toggling upvote:', err);
      alert('Erro ao votar: ' + err.message);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!userId) {
      navigate('/login');
      return;
    }

    if (!newCommentText.trim()) return;

    try {
      setSubmitting(true);
      await addComment(userId, bar.id, newCommentText.trim());
      setNewCommentText('');
      await loadBarDetails(); // Reload to get new comment
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Erro ao adicionar comentário: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyClick = (comment) => {
    if (!userId) {
      navigate('/login');
      return;
    }
    setReplyTarget(comment);
    setReplyText(`@${comment.userName || 'Anônimo'} `);
  };

  const handleAddReply = async (e) => {
    e.preventDefault();

    if (!userId) {
      navigate('/login');
      return;
    }

    if (!replyText.trim() || !replyTarget) return;

    try {
      setSubmitting(true);
      await addComment(userId, bar.id, replyText.trim(), replyTarget.id);
      setReplyTarget(null);
      setReplyText('');
      await loadBarDetails(); // Reload to get new reply
    } catch (err) {
      console.error('Error adding reply:', err);
      alert('Erro ao adicionar resposta: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <p>Carregando...</p>
      </div>
    );
  }

  if (error || !bar) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>{error || 'Bar não encontrado'}</p>
          <button onClick={() => navigate('/')}>Voltar para a lista</button>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    if (!userId) {
      navigate('/login');
      return;
    }
    navigate('/sugerir', { state: { bar } });
  };

  const musicStylesText = formatMusicStyles(bar.musicStyles);

  return (
    <div className="page-container">
      <div className="bar-detail">
        <div className="bar-detail-header">
          <h1>
            {bar.name}
            {musicStylesText && <span className="music-style-badge">{musicStylesText}</span>}
          </h1>
          {bar.neighborhood && <p className="bar-neighborhood">{bar.neighborhood}</p>}
        </div>

        {bar.notes && <p className="bar-notes">{bar.notes}</p>}

        <FeaturePills bar={bar} />

        <div className="bar-detail-footer">
          <UpvoteButton
            bar={bar}
            userId={userId}
            onToggle={handleToggleUpvote}
            onRequireLogin={() => navigate('/login')}
          />
          <div className="bar-detail-actions">
            {userId && (
              <button onClick={handleEdit} className="btn-edit">
                ✏️ Editar
              </button>
            )}
          </div>
          {bar.createdByName && (
            <span className="bar-author">Sugerido por {formatNameWithInitials(bar.createdByName)}</span>
          )}
        </div>

        <hr />

        <div className="comments-section">
          <h2>Comentários</h2>

          <CommentTree
            comments={comments}
            onReplyClick={handleReplyClick}
            canReply={!!userId}
          />

          {userId ? (
            <>
              {replyTarget && (
                <div className="reply-form">
                  <div className="reply-form-header">
                    <span>Respondendo a {replyTarget.userName ? formatNameWithInitials(replyTarget.userName) : 'Anônimo'}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyTarget(null);
                        setReplyText('');
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                  <form onSubmit={handleAddReply}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Escreva sua resposta..."
                      rows={3}
                      disabled={submitting}
                    />
                    <button type="submit" disabled={submitting || !replyText.trim()}>
                      {submitting ? 'Enviando...' : 'Enviar resposta'}
                    </button>
                  </form>
                </div>
              )}

              <form className="new-comment-form" onSubmit={handleAddComment}>
                <textarea
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Adicione um comentário..."
                  rows={3}
                  disabled={submitting}
                />
                <button type="submit" disabled={submitting || !newCommentText.trim()}>
                  {submitting ? 'Enviando...' : 'Comentar'}
                </button>
              </form>
            </>
          ) : (
            <div className="login-prompt">
              <p>Faça login para comentar</p>
              <button onClick={() => navigate('/login')}>Entrar</button>
            </div>
          )}
        </div>

        {bar.contributors && bar.contributors.length > 0 && (
          <div className="contributors-section">
            <h3>Colaboradores</h3>
            <p className="contributors-list">
              {bar.contributors.map((c, idx) => (
                <span key={idx} className="contributor-name">
                  {c.name ? formatNameWithInitials(c.name) : 'Anônimo'}
                </span>
              )).reduce((prev, curr) => [prev, ', ', curr])}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
